import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import './ChartRenderer.css';

/**
 * ChartRenderer
 *
 * Renders charts inline in an AI response. Visual vocabulary matches the
 * Lodgeic design system: compact frame, hairline border, caption at bottom,
 * Chart/Table toggle, hospitality-semantic colors (orange = primary KPI,
 * onyx = comparison / LY / budget, red = negative, green = positive).
 *
 * Supported chart types:
 *   - bar, line, pie, composed  (Recharts)
 *   - waterfall                 (SVG — variance explainers)
 *   - bullet                    (HTML/CSS — actual vs budget)
 *   - sparkline                 (SVG — trend inside a row)
 *   - rate_position             (HTML/SVG — hotel rates on a shared price axis)
 */
/**
 * Unit a category label implies for a single-series bar/line whose rows are
 * KPIs rather than members of one series ("Operating revenue", "GOP margin",
 * "Variance to budget"). Mirrors the server's inferChartUnitFromKey.
 */
const unitFromLabel = (label) => {
  const name = String(label || '').replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().trim();
  if (/\b(?:revenue per occupied room|revpor|per occupied room)\b|revenue\s*\/\s*(?:occ|occupied|orn)\s*room/.test(name)) return '$';
  if (/\b(?:occupied rooms?|rooms sold|room nights?|available rooms?|nights?|count|units?)\b/.test(name)) return '#';
  if (/\b(?:occupancy|occ|percent|percentage|pct|margin|ratio|share|flow[- ]?through|conversion)\b|%/.test(name)) return '%';
  if (/\b(?:adr|average daily rate|revpar|revenue|spend|cost|expense|amount|budget|actual|variance|pickup|otb|sales|profit|loss|income|cash|balance|gop|ebitda|payroll|labor|wages?)\b|\$/.test(name)) return '$';
  return null;
};

/**
 * A model sometimes squeezes a KPI summary — dollars, percentages and a
 * NaN — into ONE bar series ("Operating revenue $2.8M / Budget margin $9 /
 * GOP $NaN / Margin $-24", 2026-08-19). Every value then shares one axis and
 * one unit, which is nonsense. Detect that shape from the row labels /
 * magnitudes and re-express it as KPI cards, each with its own format (or a
 * table when there are too many rows). Non-finite values are dropped
 * everywhere; a series that ends up empty is not drawn.
 */
const normalizeChartConfig = (cfg) => {
  if (!cfg || typeof cfg !== 'object' || !Array.isArray(cfg.data)) return cfg;
  const yKeys = Array.isArray(cfg.yKeys) && cfg.yKeys.length ? cfg.yKeys : ['value'];
  const numericKeys = [...yKeys, ...(Array.isArray(cfg.yKeysRight) ? cfg.yKeysRight : [])];
  // 1. Drop rows whose every plotted value is non-finite (NaN/null/"NaN").
  const plottable = (row) => row && typeof row === 'object'
    && numericKeys.some((k) => Number.isFinite(Number(row[k])) && row[k] !== null && row[k] !== '');
  const cleaned = cfg.data.filter(plottable);
  const dropped = cfg.data.length - cleaned.length;
  // ...except on a dated series, where an empty row is the point. A rate
  // calendar night on which nobody published a price is a real, reportable
  // gap; deleting the row shifted the remaining nights left and silently
  // shortened a 7-night shop to 6 (competitor-rates-next7, 2026-08-20).
  // Keep the date on the axis and let the series break instead.
  const chartType = String(cfg.type || '').toLowerCase();
  const xKeyName = cfg.xKey || 'label';
  const isDatedSeries = ['line', 'composed'].includes(chartType)
    && cfg.data.length > 0
    && cfg.data.every((row) => row && typeof row === 'object' && /^\d{4}-\d{2}-\d{2}/.test(String(row[xKeyName] ?? '')));
  if (dropped > 0 && isDatedSeries) {
    const gapped = cfg.data.map((row) => {
      if (plottable(row)) return row;
      const blanked = { ...row };
      for (const key of numericKeys) blanked[key] = null;
      return blanked;
    });
    return { ...cfg, data: gapped };
  }
  let out = dropped > 0 ? { ...cfg, data: cleaned } : cfg;
  if (!['bar', 'line'].includes(String(cfg.type || '').toLowerCase())) return out;
  if (yKeys.length !== 1 || cfg.yKeysRight?.length) return out;
  const xKey = cfg.xKey || 'label';
  const yKey = yKeys[0];
  const rows = cleaned.filter((r) => Number.isFinite(Number(r[yKey])));
  if (rows.length < 2 || rows.length > 8) return out;
  // 2. Do the category labels look like DIFFERENT KPIs with different units?
  const units = rows.map((r) => unitFromLabel(r[xKey]));
  const known = units.filter(Boolean);
  const distinct = new Set(known);
  const magnitudes = rows.map((r) => Math.abs(Number(r[yKey]))).filter((v) => v > 0);
  const spread = magnitudes.length >= 2 ? Math.max(...magnitudes) / Math.min(...magnitudes) : 1;
  const mixedUnits = distinct.size >= 2 && known.length >= Math.ceil(rows.length / 2);
  const kpiShaped = /\b(margin|pct|percent|ratio|variance)\b/i.test(rows.map((r) => r[xKey]).join(' '))
    && spread > 1e3;
  if (!mixedUnits && !kpiShaped) return out;
  const axisUnit = String(cfg.yAxisType || '').toLowerCase().includes('percent') ? '%'
    : String(cfg.yAxisType || '').toLowerCase().includes('curr') ? '$' : '#';
  const formatFor = (u) => (u === '%' ? 'percentage' : u === '$' ? 'currency' : 'number');
  return {
    ...out,
    type: 'kpis',
    normalizedFrom: cfg.type,
    data: rows.map((r, i) => ({
      label: String(r[xKey] ?? ''),
      value: Number(r[yKey]),
      format: formatFor(units[i] || axisUnit),
    })),
  };
};

const ChartRenderer = ({ chartConfig: rawChartConfig }) => {
  const { isDarkMode } = useTheme();
  const chartConfig = normalizeChartConfig(rawChartConfig);
  const [view, setView] = useState('chart'); // 'chart' | 'table'

  if (!chartConfig || !chartConfig.type) return null;

  // `let`: percent series in 0–1 form are rescaled below before rendering.
  let data = Array.isArray(chartConfig.data) ? chartConfig.data : [];
  const {
    type,
    title,
    subtitle,
    caption,
    xKey,
    yKeys = ['value'],
    yAxisType = 'number',
    yKeysRight,
    yAxisTypeRight,
    seriesTypes = chartConfig.seriesTypes || chartConfig.seriesUnits || chartConfig.metricTypes || chartConfig.metricFormats || {},
    colors,
  } = chartConfig;
  // Horizontal bar charts (bullet-bar style) render per-row — size dynamically.
  // Vertical charts keep the design's compact 220–240px frame.
  const isSingleSeriesBar = type === 'bar' && yKeys.length === 1;
  const shouldGoHorizontal = chartConfig.layout === 'horizontal'
    || (isSingleSeriesBar && Array.isArray(data) && data.length >= 5);
  const height =
    type === 'sparkline' ? (chartConfig.sparkHeight || 60)
    : shouldGoHorizontal ? Math.max(160, Math.min(320, data.length * 32 + 28))
    : 220;

  // Palette — Lodgeic design-system semantics: Lodge Orange = primary KPI,
  // onyx = comparison (LY/LW/budget), green = favorable, muted red =
  // unfavorable, blue = fifth-wheel. Max 3 series per the design rules.
  const LODGE_COLORS = colors || [
    '#FF7A00', // lodge-orange-400 — current/highlighted series
    isDarkMode ? '#D0D0DC' : '#26262D', // onyx comparison (LY/LW/budget) — flipped for dark surfaces
    '#1F7A4C', // favorable
    '#E27474', // unfavorable (design literal)
    '#3A6EA5', // other
    '#9AA0AB', // muted filler
  ];

  const GRID = isDarkMode ? 'rgba(255,255,255,0.08)' : '#EEEEF0';
  const AXIS = isDarkMode ? '#A0A0AE' : '#6B7280';

  // ---- formatters ----
  const makeAxisFormatter = (axisType) => (value) => {
    if (value == null) return '';
    if (axisType === 'currency') {
      if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
      if (Math.abs(value) >= 1000) return `$${Math.round(value / 1000)}K`;
      return `$${Math.round(value)}`;
    }
    if (axisType === 'percentage') return Math.abs(value) < 10 && !Number.isInteger(value) ? `${Number(value).toFixed(1)}%` : `${Math.round(value)}%`;
    if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (Math.abs(value) >= 1000) return `${Math.round(value / 1000)}K`;
    return Math.round(value).toString();
  };
  const makeTooltipFormatter = (axisType) => (value) => {
    if (value == null) return '';
    if (axisType === 'currency') return `$${value.toLocaleString()}`;
    if (axisType === 'percentage') return `${Number(value).toFixed(1)}%`;
    return Number(value).toLocaleString();
  };
  const normalizeAxisType = (axisType) => {
    const normalized = String(axisType || '').trim().toLowerCase();
    if (['$', 'usd', 'dollar', 'dollars', 'money', 'currency', 'currency-expense'].includes(normalized)) return 'currency';
    if (['%', 'percent', 'percentage', 'pct', 'point', 'points'].includes(normalized)) return 'percentage';
    if (['#', 'number', 'count', 'integer', 'numeric', 'rooms', 'room-nights'].includes(normalized)) return 'number';
    return null;
  };
  const metricName = (key) => String(key || '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  const seriesTypeForKey = (key) => {
    const explicit = normalizeAxisType(seriesTypes?.[key]);
    if (explicit) return explicit;
    if (Array.isArray(yKeysRight) && yKeysRight.includes(key)) return normalizeAxisType(yAxisTypeRight) || 'number';
    if (Array.isArray(yKeys) && yKeys.includes(key)) return normalizeAxisType(yAxisType) || 'number';

    const name = metricName(key);
    if (/\b(?:revenue per occupied room|revpor|por|per occupied room)\b|revenue\s*\/\s*(?:occ|occupied|orn)\s*room|\$\s*\/\s*(?:occ|occupied|orn)/.test(name)) return 'currency';
    if (/\b(?:occupied rooms?|rooms sold|room nights?|available rooms?)\b/.test(name)) return 'number';
    if (/\b(?:occupancy|occ|percent|percentage|pct|margin|ratio|share)\b/.test(name)) return 'percentage';
    if (/\b(?:adr|average daily rate|revpar|revenue|spend|cost|expense|amount|budget|actual|variance|pickup|otb|sales|profit|loss|income|cash|balance)\b/.test(name)) return 'currency';
    return 'number';
  };
  const formatSeriesValue = (key, value, axisFormatter = false) => {
    const axisType = seriesTypeForKey(key);
    return (axisFormatter ? makeAxisFormatter(axisType) : makeTooltipFormatter(axisType))(value);
  };
  const formatYAxis = makeAxisFormatter(yAxisType);
  const formatTooltipValue = makeTooltipFormatter(yAxisType);

  // ---- percentage scale normalization ----
  // Percent series arrive in two conventions: 0–100 ("62.5") and 0–1
  // ("0.625", straight from a ratio column). The formatters assume 0–100, so
  // a fraction series renders as an axis of 0/0/1/1 and a tooltip of "0.6%"
  // (observed 2026-08-18, monthly occupancy). Detect a whole-series
  // fraction and scale it once, here, rather than trusting the model to
  // remember. Heuristic: every finite value in the series has |v| <= 1.5
  // (a real 0–100 percent series with every point at or below 1.5% is not a
  // chart anyone draws).
  const percentKeys = [...(Array.isArray(yKeys) ? yKeys : []), ...(Array.isArray(yKeysRight) ? yKeysRight : [])]
    .filter((k) => seriesTypeForKey(k) === 'percentage');
  const fractionKeys = percentKeys.filter((k) => {
    const vals = (Array.isArray(data) ? data : []).map((row) => Number(row?.[k])).filter(Number.isFinite);
    return vals.length > 0 && vals.every((v) => Math.abs(v) <= 1.5) && vals.some((v) => !Number.isInteger(v));
  });
  if (fractionKeys.length && Array.isArray(data)) {
    data = data.map((row) => {
      const out = { ...row };
      for (const k of fractionKeys) {
        const v = Number(row?.[k]);
        if (Number.isFinite(v)) out[k] = Math.round(v * 1000) / 10;
      }
      return out;
    });
  }

  // ---- dual-axis plan ----
  // Dual axes kick in when the caller explicitly sets `yKeysRight`, OR when
  // there are exactly 2 yKeys and the magnitudes differ by ≥ 8× (e.g. Room
  // Revenue in thousands $ plotted against Rooms Sold in tens). Only applies
  // to bar (vertical multi-series) / line / composed — pie, waterfall,
  // bullet, sparkline, kpis ignore this.
  const supportsDualAxis = ['bar', 'line', 'composed'].includes(type)
    && !(type === 'bar' && shouldGoHorizontal)
    && yKeys.length >= 2;

  const computeDualAxisPlan = () => {
    if (!supportsDualAxis) return null;

    const explicitRight = Array.isArray(yKeysRight) && yKeysRight.length > 0;
    let rightSet;
    let rightType;

    if (explicitRight) {
      rightSet = new Set(yKeysRight.filter((k) => yKeys.includes(k)));
      if (rightSet.size === 0 || rightSet.size === yKeys.length) return null;
      rightType = yAxisTypeRight || 'number';
    } else if (yKeys.length === 2 && Array.isArray(data) && data.length > 0) {
      // Auto-detect when magnitudes are vastly different.
      const maxAbs = (k) => data.reduce((acc, row) => {
        const v = Number(row?.[k]);
        return Number.isFinite(v) ? Math.max(acc, Math.abs(v)) : acc;
      }, 0);
      const a = maxAbs(yKeys[0]);
      const b = maxAbs(yKeys[1]);
      if (!(a > 0 && b > 0)) return null;
      const ratio = Math.max(a, b) / Math.min(a, b);
      if (ratio < 8) return null;
      const smaller = a >= b ? yKeys[1] : yKeys[0];
      rightSet = new Set([smaller]);
      rightType = 'number';
    } else {
      return null;
    }

    const leftKeys = yKeys.filter((k) => !rightSet.has(k));
    const rightKeys = yKeys.filter((k) => rightSet.has(k));
    return {
      leftKeys,
      rightKeys,
      leftType: yAxisType,
      rightType,
      formatLeftAxis: makeAxisFormatter(yAxisType),
      formatRightAxis: makeAxisFormatter(rightType),
      formatLeftTip: makeTooltipFormatter(yAxisType),
      formatRightTip: makeTooltipFormatter(rightType),
      isRight: (k) => rightSet.has(k),
    };
  };
  const dualAxis = computeDualAxisPlan();

  const formatEntryValue = (entry) => {
    if (entry?.dataKey) return formatSeriesValue(entry.dataKey, entry.value);
    if (dualAxis && dualAxis.isRight(entry.dataKey)) return dualAxis.formatRightTip(entry.value);
    return formatTooltipValue(entry.value);
  };
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="ai-chart-tooltip">
        <div className="ai-chart-tooltip-label">{label}</div>
        {payload.map((entry, i) => (
          <div key={i} className="ai-chart-tooltip-value" style={{ color: entry.color }}>
            <span className="ai-chart-tooltip-dot" style={{ background: entry.color }} />
            {entry.name}: {formatEntryValue(entry)}
          </div>
        ))}
      </div>
    );
  };

  // Pretty-print code-style keys ("roomRevenue", "fb_revenue") to
  // human-readable labels ("Room revenue", "Fb revenue").
  const prettyKey = (k) => {
    if (!k || typeof k !== 'string') return String(k ?? '');
    return k
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^./, (c) => c.toUpperCase());
  };

  // Legend rendered as HTML OUTSIDE the Recharts SVG. Pulling it out of the
  // Recharts tree is necessary because Recharts' internal Legend can eat the
  // plot area on tight multi-series layouts, causing axes to collapse.
  const renderLegend = () => {
    const isMulti = (yKeys?.length || 0) > 1;
    if (!isMulti) return null;
    return (
      <div className="ai-chart-legend">
        {yKeys.map((k, i) => {
          // Guide: first series = solid swatch (current), rest = dashed rule
          // for time-series / line-family charts to signal a comparison line.
          const isDashed = (type === 'line') && i > 0;
          const color = isDashed
            ? (isDarkMode ? '#8888A0' : '#A0A0AE')
            : LODGE_COLORS[i % LODGE_COLORS.length];
          return (
            <span key={k} className="ai-chart-legend-item">
              {isDashed ? (
                <span
                  className="ai-chart-legend-swatch is-dash"
                  style={{ borderColor: color }}
                />
              ) : (
                <span
                  className="ai-chart-legend-swatch"
                  style={{ background: color }}
                />
              )}
              {prettyKey(k)}
            </span>
          );
        })}
      </div>
    );
  };

  // ---- chart renderers ----
  const renderRecharts = (typeOverride) => {
    const chartType = typeOverride || type;
    // Recharts 2.x does not unwrap React Fragments when scanning children —
    // it uses findChildByType() on direct props.children. Wrapping
    // XAxis/YAxis/CartesianGrid/Tooltip in <>...</> meant Recharts saw a
    // single Fragment child and emitted NO axis elements into the SVG.
    // commonXY is built as an array so the parent chart can spread it with
    // {commonXY} — each element becomes a direct child of the chart.
    const X_AXIS_HEIGHT = 32;
    const Y_AXIS_WIDTH = 60;
    const commonXY = [
      <CartesianGrid key="grid" stroke={GRID} strokeDasharray="2 3" vertical={false} />,
      <XAxis
        key="x"
        dataKey={xKey}
        height={X_AXIS_HEIGHT}
        tickFormatter={(v) => String(v ?? '')}
        fontSize={11}
      />,
      dualAxis ? (
        <YAxis
          key="y-left"
          yAxisId="left"
          width={Y_AXIS_WIDTH}
          tickFormatter={dualAxis.formatLeftAxis}
          fontSize={11}
        />
      ) : (
        <YAxis
          key="y"
          width={Y_AXIS_WIDTH}
          tickFormatter={formatYAxis}
          fontSize={11}
        />
      ),
      dualAxis ? (
        <YAxis
          key="y-right"
          yAxisId="right"
          orientation="right"
          width={Y_AXIS_WIDTH}
          tickFormatter={dualAxis.formatRightAxis}
          fontSize={11}
        />
      ) : null,
      <Tooltip key="tt" content={<CustomTooltip />} cursor={{ fill: 'rgba(255,122,0,0.06)' }} />,
    ].filter(Boolean);
    // Recharts enforces an exact yAxisId match between each graphical item
    // (Bar / Line / Area) and a rendered YAxis. In single-axis mode we omit
    // the prop entirely (passing `yAxisId={undefined}` has been observed to
    // trigger an invariant failure). In dual-axis mode we always emit a
    // concrete 'left' or 'right' id so every item binds to its axis.
    const axisPropFor = (k) => (dualAxis ? { yAxisId: dualAxis.isRight(k) ? 'right' : 'left' } : {});

    // Value-label renderer positioned on top of a vertical bar.
    const renderTopLabel = (props) => {
      const { x, y, width, value } = props;
      if (value == null) return null;
      return (
        <text
          x={x + width / 2}
          y={y - 5}
          fill="var(--lodge-onyx-900, #0F0F11)"
          fontSize={10.5}
          fontWeight="600"
          textAnchor="middle"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatYAxis(value)}
        </text>
      );
    };

    switch (chartType) {
      case 'bar': {
        // Horizontal (bullet-bar-style) layout for segment / property comparisons
        if (shouldGoHorizontal) {
          const yKey = yKeys[0];
          const labelWidth = Math.min(160, Math.max(80, Math.max(...data.map(d => (d[xKey] || '').length)) * 7 + 12));
          const AXIS_LINE_COLOR_H = isDarkMode ? 'rgba(255,255,255,0.22)' : 'rgba(15,15,17,0.22)';
          return (
            <ResponsiveContainer width="100%" height={height + 40}>
              <BarChart
                data={data}
                layout="vertical"
                margin={{ top: 4, right: 56, left: 0, bottom: 32 }}
              >
                <CartesianGrid stroke={GRID} strokeDasharray="2 3" horizontal={false} />
                <XAxis
                  type="number"
                  stroke={AXIS_LINE_COLOR_H}
                  fontSize={11}
                  tickLine={{ stroke: AXIS_LINE_COLOR_H }}
                  axisLine={{ stroke: AXIS_LINE_COLOR_H }}
                  tickFormatter={formatYAxis}
                  tick={{ fill: AXIS, fontSize: 11 }}
                  tickSize={4}
                />
                <YAxis
                  type="category"
                  dataKey={xKey}
                  stroke={AXIS_LINE_COLOR_H}
                  fontSize={11.5}
                  tickLine={false}
                  axisLine={{ stroke: AXIS_LINE_COLOR_H }}
                  width={labelWidth}
                  tick={{ fill: 'var(--lodge-onyx-800, #1A1A1F)', fontSize: 11.5 }}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,122,0,0.06)' }} />
                <Bar dataKey={yKey} fill={LODGE_COLORS[0]} radius={[3, 3, 3, 3]} barSize={16}>
                  <LabelList
                    dataKey={yKey}
                    position="right"
                    formatter={formatYAxis}
                    style={{ fill: 'var(--lodge-onyx-900, #0F0F11)', fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          );
        }
        // Vertical bars — value labels on top for self-explanation.
        // Legend is rendered OUTSIDE the Recharts chart (via renderLegend())
        // so it doesn't eat from the plot area. Chart height now only needs
        // to account for margin + X-axis (32px) + a small buffer.
        const isMulti = yKeys.length > 1;
        const vertHeight = height + 40;
        const bottomMargin = 16;
        const topMargin = isMulti ? 10 : 22;
        return (
          <ResponsiveContainer width="100%" height={vertHeight}>
            <BarChart data={data} margin={{ top: topMargin, right: 12, left: 4, bottom: bottomMargin }}>
              {commonXY}
              {yKeys.map((k, i) => (
                <Bar key={k} dataKey={k} {...axisPropFor(k)} fill={LODGE_COLORS[i % LODGE_COLORS.length]} radius={[4, 4, 0, 0]} minPointSize={3}>
                  {!isMulti && <LabelList dataKey={k} content={renderTopLabel} />}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      }
      case 'line': {
        // Guide spec: yKeys[0] is the current series — orange + gradient area
        // fill. yKeys[1+] are dashed gray comparison lines (LY / LW / budget).
        // ComposedChart is required because Recharts' AreaChart rejects <Line>
        // children (only <Area> renders).
        const gradId = `lodgeArea-${Math.random().toString(36).slice(2, 8)}`;
        return (
          <ResponsiveContainer width="100%" height={height + 40}>
            <ComposedChart data={data} margin={{ top: 12, right: 12, left: 4, bottom: 16 }}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={LODGE_COLORS[0]} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={LODGE_COLORS[0]} stopOpacity={0} />
                </linearGradient>
              </defs>
              {commonXY}
              {yKeys.map((k, i) => {
                // Per the chart-library spec: current period = orange, every
                // comparison line = dashed onyx-300 neutral (not near-black).
                const color = i === 0
                  ? LODGE_COLORS[0]
                  : (isDarkMode ? '#8888A0' : '#A0A0AE');
                const axisProps = axisPropFor(k);
                if (i === 0) {
                  return (
                    <Area key={k} type="monotone" dataKey={k} {...axisProps} stroke={color}
                      strokeWidth={2.4} fill={`url(#${gradId})`}
                      dot={false}
                      activeDot={{ r: 4 }} />
                  );
                }
                return (
                  <Line key={k} type="monotone" dataKey={k} {...axisProps} stroke={color}
                    strokeWidth={1.6} strokeDasharray="4 4"
                    dot={false} activeDot={{ r: 3 }} />
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        );
      }
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <PieChart>
              <Pie data={data} dataKey={yKeys[0]} nameKey={xKey} cx="50%" cy="50%"
                outerRadius={Math.min(height / 2 - 20, 90)}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                labelLine={false}>
                {data.map((_, i) => <Cell key={i} fill={LODGE_COLORS[i % LODGE_COLORS.length]} />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        );
      case 'composed':
        return (
          <ResponsiveContainer width="100%" height={height + 40}>
            <ComposedChart data={data} margin={{ top: 12, right: 12, left: 4, bottom: 16 }}>
              {commonXY}
              {yKeys.slice(0, Math.ceil(yKeys.length / 2)).map((k, i) => (
                <Bar key={k} dataKey={k} {...axisPropFor(k)} fill={LODGE_COLORS[i % LODGE_COLORS.length]} radius={[4, 4, 0, 0]} />
              ))}
              {yKeys.slice(Math.ceil(yKeys.length / 2)).map((k, i) => {
                const color = LODGE_COLORS[(i + Math.ceil(yKeys.length / 2)) % LODGE_COLORS.length];
                return (
                  <Line key={k} type="monotone" dataKey={k} {...axisPropFor(k)} stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  // ---- Waterfall: data = [{label, value, kind: 'start'|'pos'|'neg'|'end'}] ----
  const renderWaterfall = () => {
    const items = data;
    if (!items.length) return null;
    const W = 620, H = height, PAD_L = 8, PAD_R = 8, PAD_T = 26, PAD_B = 36;
    const innerW = W - PAD_L - PAD_R, innerH = H - PAD_T - PAD_B;
    const colW = Math.max(10, innerW / items.length - 10);

    let running = 0;
    const bars = items.map((it, i) => {
      let base, top;
      if (it.kind === 'start' || it.kind === 'end') {
        base = 0; top = it.value; running = it.value;
      } else {
        base = running; top = running + it.value; running = top;
      }
      return { ...it, base, top, i };
    });
    const vals = bars.flatMap(b => [b.base, b.top]);
    const max = Math.max(...vals, 0);
    const min = Math.min(...vals, 0);
    const range = max - min || 1;
    const y = (v) => PAD_T + (1 - (v - min) / range) * innerH;
    const zeroY = y(0);
    const x = (i) => PAD_L + i * (innerW / items.length) + 5;
    const fmtDelta = (n) => (n >= 0 ? '+' : '−') + formatYAxis(Math.abs(n)).replace(/^\$?-/, '$');
    const fmtTotal = (n) => formatYAxis(Math.abs(n));

    return (
      <svg className="ai-wf" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
        <line className="wf-zero" x1={PAD_L} x2={W - PAD_R} y1={zeroY} y2={zeroY} />
        {bars.map((b, i) => {
          const yTop = Math.min(y(b.top), y(b.base));
          const h = Math.max(1, Math.abs(y(b.top) - y(b.base)));
          const cls = b.kind === 'start' || b.kind === 'end' ? 'wf-total' : (b.value >= 0 ? 'wf-pos' : 'wf-neg');
          let connector = null;
          if (i > 0 && b.kind !== 'start') {
            const prev = bars[i - 1];
            connector = <line className="wf-connector" x1={x(i - 1) + colW} x2={x(i)} y1={y(prev.top)} y2={y(prev.top)} />;
          }
          const labelY = yTop - 5;
          const labelVal = (b.kind === 'start' || b.kind === 'end') ? fmtTotal(b.value) : fmtDelta(b.value);
          const words = String(b.label || '').split(' ');
          return (
            <g key={i}>
              {connector}
              <rect className={cls} x={x(i)} y={yTop} width={colW} height={h} rx={2} />
              <text className="wf-val" x={x(i) + colW / 2} y={labelY} textAnchor="middle">{labelVal}</text>
              <text className="wf-lab" x={x(i) + colW / 2} y={H - 20} textAnchor="middle">{words.slice(0, 2).join(' ')}</text>
              {words.length > 2 && <text className="wf-lab" x={x(i) + colW / 2} y={H - 6} textAnchor="middle">{words.slice(2).join(' ')}</text>}
            </g>
          );
        })}
      </svg>
    );
  };

  // ---- BulletBars: data = [{label, actual, budget, max?}] ----
  // Design pattern: neutral track, dashed budget bracket spanning 0→budget,
  // orange actual fill (muted red when unfavorable), onyx target tick.
  const renderBullets = () => {
    return (
      <div className="ai-bullets">
        {data.map((r, i) => {
          const max = r.max || Math.max(r.actual, r.budget) * 1.15;
          const actualPct = Math.min(100, (r.actual / max) * 100);
          const budgetPct = Math.min(100, (r.budget / max) * 100);
          const variance = r.actual - r.budget;
          const overIsBad = chartConfig.direction !== 'higher-is-better';
          const isNeg = overIsBad ? variance > 0 : variance < 0;
          return (
            <div className="ai-bullet" key={i}>
              <div className="ai-bullet-label">{r.label}</div>
              <div className="ai-bullet-track">
                <div className="ai-bullet-budget" style={{ width: budgetPct + '%' }} />
                <div className={'ai-bullet-actual' + (isNeg ? ' is-over' : '')} style={{ width: actualPct + '%' }} />
                <div className="ai-bullet-target" style={{ left: `calc(${budgetPct}% - 1px)` }} />
              </div>
              <div className={'ai-bullet-var ' + (isNeg ? 'neg' : 'pos')}>
                {variance >= 0 ? '+' : '−'}{formatYAxis(Math.abs(variance))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ---- KPI row: data = [{label, value, delta?, direction?: 'up'|'down', format?}] ----
  // Guide pattern: 2–4 cards, DM Serif headline value, delta with arrow in
  // green/red. Used for "portfolio summary" / "how are we doing" questions.
  const renderKpis = () => {
    // A KPI list re-expressed from a mixed-unit bar keeps every row (≤8).
    const cards = Array.isArray(data) ? data.slice(0, chartConfig.normalizedFrom ? 8 : 4) : [];
    if (!cards.length) return null;
    const fmtValue = (v, fmt) => {
      if (v == null) return '—';
      if (typeof v === 'string') return v;
      if (fmt === 'currency') return formatYAxis(v).startsWith('$') ? formatYAxis(v) : `$${formatYAxis(v)}`;
      if (fmt === 'percentage') return `${Number(v).toFixed(1)}%`;
      return formatYAxis(v);
    };
    return (
      <div className="ai-kpis">
        {cards.map((c, i) => {
          const dir = c.direction || (typeof c.delta === 'string'
            ? (c.delta.trim().startsWith('-') || c.delta.trim().startsWith('−') ? 'down' : 'up')
            : (typeof c.delta === 'number' ? (c.delta >= 0 ? 'up' : 'down') : null));
          const polarity = c.polarity || (c.format === 'currency-expense' ? 'lower-is-better' : 'higher-is-better');
          const isGood = dir && ((polarity === 'higher-is-better' && dir === 'up') || (polarity === 'lower-is-better' && dir === 'down'));
          return (
            <div className="ai-kpi" key={i}>
              <div className="ai-kpi-label">{c.label}</div>
              <div className="ai-kpi-value">{fmtValue(c.value, c.format)}</div>
              {c.delta != null && (
                <div className={'ai-kpi-delta ' + (dir === 'up' ? 'up ' : dir === 'down' ? 'down ' : '') + (isGood ? 'good' : 'bad')}>
                  {dir === 'up' ? '↑ ' : dir === 'down' ? '↓ ' : ''}
                  {typeof c.delta === 'number' ? fmtValue(c.delta, c.format) : c.delta}
                  {c.deltaSuffix ? ` ${c.deltaSuffix}` : ''}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ---- Sparkline: single inline — data = [numbers], optional ly = [numbers] ----
  const renderSparkline = () => {
    const cur = Array.isArray(data) ? data : (data.current || []);
    const ly = chartConfig.ly || data.ly;
    if (!cur.length) return null;
    const W = chartConfig.width || 320, H = chartConfig.sparkHeight || 60, PAD = 3;
    const all = [...cur, ...(ly || [])];
    const max = Math.max(...all), min = Math.min(...all);
    const range = max - min || 1;
    const toPt = (arr) => arr.map((v, i) => {
      const px = PAD + (i / (arr.length - 1)) * (W - PAD * 2);
      const py = PAD + (1 - (v - min) / range) * (H - PAD * 2);
      return [px, py];
    });
    const toPath = (pts) => pts.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');
    const curPts = toPt(cur);
    const lyPts = ly ? toPt(ly) : null;
    const last = curPts[curPts.length - 1];
    return (
      <svg className="ai-spark" viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H }}>
        {lyPts && <path className="spark-ly" d={toPath(lyPts)} />}
        <path className="spark-cur" d={toPath(curPts)} />
        <circle className="spark-dot" cx={last[0]} cy={last[1]} r={2.5} />
      </svg>
    );
  };

  // ---- Rate position: data = [{name, value, source?, subject?}] ----
  // Shows distribution statistics and every hotel on one comparable price axis.
  const renderRatePosition = () => {
    const rawRows = Array.isArray(data) ? data : [];
    // A $0 (or negative) rate is always a fabricated placeholder for a hotel
    // with no public price — never a real rate. It must not become a dot or
    // drag LOW/MEDIAN down, so only strictly-positive values plot.
    const points = rawRows
      .map((row) => ({ ...row, value: Number(row.value) }))
      .filter((row) => Number.isFinite(row.value) && row.value > 0);
    // Contract is [{name, value, subject}]. If the model emitted series-shaped
    // rows instead (e.g. {date, "Hotel A": 190, ...} for a multi-night shop),
    // degrade to the standard Recharts line rather than an error card.
    if (!points.length) {
      const looksLikeSeries = rawRows.length > 0
        && Object.values(rawRows[0]).some((v) => typeof v === 'number' && v > 0);
      if (looksLikeSeries) return renderRecharts('line');
      // Empty shop result — show an honest empty state, not the
      // "Unsupported chart type" error card.
      return <div className="ai-rate-position-empty">No public rates were returned for this shop.</div>;
    }
    const sorted = points.map((row) => row.value).sort((a, b) => a - b);
    const low = sorted[0];
    const max = sorted[sorted.length - 1];
    const average = sorted.reduce((sum, value) => sum + value, 0) / sorted.length;
    const middle = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
    const range = max - low || 1;
    const pct = (value) => 4 + ((value - low) / range) * 92;

    // Label lanes: assign by price rank (not input order) so hotels sitting
    // within a few dollars of each other always alternate above/below rows
    // and their labels never overlap. When two SAME-lane neighbours are still
    // closer than ~9% of the axis, fan out to a third lane.
    const ranked = [...points].sort((a, b) => a.value - b.value);
    const rankOf = new Map(ranked.map((row, i) => [row, i]));
    let laneCount = 2;
    for (let i = 2; i < ranked.length; i++) {
      if (pct(ranked[i].value) - pct(ranked[i - 2].value) < 9) { laneCount = 3; break; }
    }

    // Stat tiles per the warm-editorial spec: LOW / MEDIAN / YOU / HIGH where
    // YOU is the subject property's rate (orange). When the subject hotel is
    // unpriced (its row was filtered as a placeholder), YOU shows a muted "—";
    // if the shop never flagged a subject at all, fall back to AVG.
    const subject = points.find((row) => row.subject);
    const hadSubjectRow = rawRows.some((row) => row && row.subject);
    const stats = subject
      ? [['Low', low], ['Median', median], ['You', subject.value, true], ['High', max]]
      : hadSubjectRow
        ? [['Low', low], ['Median', median], ['You', null, true], ['High', max]]
        : [['Low', low], ['Median', median], ['Avg', average], ['High', max]];

    return (
      <div className="ai-rate-position">
        <div className="ai-rate-stats">
          {stats.map(([label, value, isYou]) => (
            <div className={`ai-rate-stat${isYou ? ' is-you' : ''}`} key={label}>
              <span>{label}</span>
              <strong className={value == null ? 'is-empty' : ''}>{value == null ? '—' : formatYAxis(value)}</strong>
            </div>
          ))}
        </div>
        <div className={`ai-rate-scale${laneCount === 3 ? ' lanes-3' : ''}`} aria-label={`Hotel rates from ${formatYAxis(low)} to ${formatYAxis(max)}`}>
          <div className="ai-rate-track" />
          <div className="ai-rate-median" style={{ left: `${pct(median)}%` }} title={`Median ${formatYAxis(median)}`} />
          {points.map((row, index) => (
            <div
              className={`ai-rate-point lane-${rankOf.get(row) % laneCount}${row.subject ? ' is-subject' : ''}`}
              style={{ left: `${pct(row.value)}%` }}
              key={`${row.name}-${index}`}
              title={`${row.name}: ${formatYAxis(row.value)}${row.source ? ` (${row.source})` : ''}`}
            >
              <span className="ai-rate-dot" />
              <span className="ai-rate-point-value">{formatYAxis(row.value)}</span>
              <span className="ai-rate-point-name">{row.name}</span>
            </div>
          ))}
          <span className="ai-rate-axis-label is-low">{formatYAxis(low)}</span>
          <span className="ai-rate-axis-label is-high">{formatYAxis(max)}</span>
        </div>
        <div className="ai-rate-legend"><span className="ai-rate-legend-subject" /> Selected property <span className="ai-rate-legend-comp" /> Competitor</div>
      </div>
    );
  };

  // ---- table fallback (for Chart/Table toggle) ----
  const renderTable = () => {
    if (!Array.isArray(data) || data.length === 0) return null;
    // For waterfall/bullet we render the known columns; for recharts we use xKey/yKeys
    if (type === 'waterfall') {
      return (
        <table className="ai-chart-table">
          <thead><tr><th>Item</th><th>Value</th><th>Type</th></tr></thead>
          <tbody>{data.map((r, i) => (
            <tr key={i}><td>{r.label}</td><td>{formatTooltipValue(r.value)}</td><td>{r.kind}</td></tr>
          ))}</tbody>
        </table>
      );
    }
    if (type === 'bullet') {
      return (
        <table className="ai-chart-table">
          <thead><tr><th>Property</th><th>Actual</th><th>Budget</th><th>Variance</th></tr></thead>
          <tbody>{data.map((r, i) => (
            <tr key={i}>
              <td>{r.label}</td>
              <td>{formatTooltipValue(r.actual)}</td>
              <td>{formatTooltipValue(r.budget)}</td>
              <td className={r.actual - r.budget >= 0 ? 'pos' : 'neg'}>
                {(r.actual - r.budget >= 0 ? '+' : '−') + formatYAxis(Math.abs(r.actual - r.budget))}
              </td>
            </tr>
          ))}</tbody>
        </table>
      );
    }
    if (type === 'sparkline') return null;
    // Default table — show ALL columns from data (not just xKey/yKeys) so
    // tables promoted from a markdown source keep their full context (e.g.
    // segment, revenue, % of total). Numeric values get tabular formatting.
    const cols = Object.keys(data[0]);
    const isNumeric = (v) => typeof v === 'number' && !Number.isNaN(v);
    return (
      <table className="ai-chart-table">
        <thead><tr>{cols.map(c => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>{data.map((r, i) => (
          <tr key={i}>{cols.map(c => (
            <td key={c} style={isNumeric(r[c]) ? { textAlign: 'right' } : undefined}>
              {isNumeric(r[c]) ? formatSeriesValue(c, r[c]) : String(r[c] ?? '')}
            </td>
          ))}</tr>
        ))}</tbody>
      </table>
    );
  };

  const chartEl =
    type === 'waterfall' ? renderWaterfall() :
    type === 'bullet'    ? renderBullets()   :
    type === 'sparkline' ? renderSparkline() :
    type === 'kpis'      ? renderKpis()      :
    type === 'rate_position' ? renderRatePosition() :
    renderRecharts();

  // External legend only applies to Recharts chart types (line/bar/composed/pie).
  const needsLegend = ['line', 'bar', 'composed'].includes(type);
  const externalLegend = needsLegend ? renderLegend() : null;

  if (!chartEl) return <div className="ai-chart-error">Unsupported chart type: {type}</div>;

  const hasToggle = type !== 'sparkline' && Array.isArray(data) && data.length > 0;

  return (
    <div className="ai-chart">
      {(title || subtitle || hasToggle) && (
        <div className="ai-chart-head">
          <div className="ai-chart-head-l">
            {title && <div className="ai-chart-title">{title}</div>}
            {subtitle && <div className="ai-chart-sub">{subtitle}</div>}
          </div>
          {hasToggle && (
            <div className="ai-chart-toggle">
              <button className={view === 'chart' ? 'is-on' : ''} onClick={() => setView('chart')}>Chart</button>
              <button className={view === 'table' ? 'is-on' : ''} onClick={() => setView('table')}>Table</button>
            </div>
          )}
        </div>
      )}
      <div className="ai-chart-body">
        {(view === 'chart' || type === 'sparkline') ? (
          <>
            {chartEl}
            {externalLegend}
          </>
        ) : renderTable()}
      </div>
      {caption && <div className="ai-chart-caption">{caption}</div>}
    </div>
  );
};

export { normalizeChartConfig, unitFromLabel };
export default ChartRenderer;
